export const Pawsible_Intro = {
    missionBrandPromise: {
        title: "Our Mission & Brand Promise",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/missionBrandPromiseTitle.png",
        body: `
          <section>
            <h3>💖 Why We Exist</h3>
            <p><span class="highlight">Pawsible isn’t just where pets stay — it’s where they’re understood.</span></p>
            <p>Our mission is simple: <strong>Safe pets. Happy people. Strong bonds.</strong> We lead the pet care industry with heart, ethics, and emotional intelligence.</p>
            <p>Everything we do — from the way we greet dogs to how we sanitize a crate — is rooted in our core mantra:</p>
            <ul>
              <li>🛡️ <strong>Safety</strong> comes first — always.</li>
              <li>🧼 <strong>Cleanliness</strong> is a non-negotiable — because health and comfort depend on it.</li>
              <li>❤️ <strong>Love</strong> makes it Pawsible — it’s the warmth and care that sets us apart.</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/missionBrandPromiseSCL.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🔑 What We Stand For</h4>
            <p>Our values guide every decision. They’re not just posters — they’re how we show up every day:</p>
            <ul>
              <li>🐾 <strong>Compassion over convenience</strong> – We slow down for scared dogs. We listen to worried pet parents. We care first.</li>
              <li>🌱 <strong>Progress over perfection</strong> – Mistakes happen. We learn, grow, and help each other get better every day.</li>
              <li>📚 <strong>Education over ego</strong> – We ask questions. We stay curious. We share what we know — no gatekeeping here.</li>
              <li>💬 <strong>Clarity over chaos</strong> – We communicate early, kindly, and honestly — with clients, coworkers, and ourselves.</li>
            </ul>
          </section>
      
          <section>
            <h4>🌟 Your Role in the Brand</h4>
            <p><span class="highlight">You are the brand.</span> Whether you’re clipping nails, leading a group walk, or answering the phone, you represent what Pawsible stands for.</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/missionBrandPromiseRoleBrand.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            <p>Your calm energy creates emotional safety. Your details make the experience feel personal. Your teamwork makes this place feel like home — for both pets and people.</p>
          </section>
      
          <section>
            <h4>🧭 The Promise in Action</h4>
            <p>When we say “Safe pets. Happy people. Strong bonds,” here’s what it looks like on the floor:</p>
            <ul>
              <li>🐶 You notice a new pup pacing and add a comfort crate without being asked</li>
              <li>📞 You call a parent after a minor scuffle with empathy and clarity</li>
              <li>🧼 You wipe down a station even when it’s not “your job”</li>
              <li>🎓 You mentor a new team member just like someone did for you</li>
            </ul>
            <p>That’s Pawsible. And that’s the brand — lived out by you.</p>
          </section>
      
          <section>
            <h4>🌱 Takeaway</h4>
            <p><span class="highlight-green">You don’t just work at Pawsible. You build it every day.</span> Lead with calm. Prioritize safety. Make love visible — and we’ll keep earning the trust of every pet and parent who walks through our doors.</p>
          </section>
        `
    },   
    emotionalStorytelling: {
        title: "Emotional Connection & Storytelling in Pet Care",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emotionalStorytellingTitle.png",
        body: `
          <section>
            <h3>📖 Why It Matters</h3>
            <p>We don’t just care for pets — <span class="highlight">we tell their stories</span>. Every interaction is a chance to show pet parents that we see, value, and celebrate their companions. <span class="highlight-green">Trust grows when love feels personal.</span></p>
          </section>
      
          <section>
            <h4>✨ Storytelling in Action</h4>
            <ul>
              <li>📸 A playful photo + caption: <em>“Max made 3 new friends today — a social butterfly in the making!”</em></li>
              <li>📩 A daily progress note: <em>“Luna explored the cat tree for the first time — huge win!”</em></li>
              <li>🎉 Graduation shoutout: <em>“Waffles rocked her recall cue today — confetti all around!”</em></li>
            </ul>
            <p>These bite-sized stories <span class="highlight-yellow">build emotional connection</span> and reinforce that every pet is seen as an individual, not a number.</p>
          </section>
      
          <section>
            <h4>💌 Communication With Heart</h4>
            <p>Whether it’s a handoff note, text, or social post, your language should reflect <span class="highlight-blue">warmth, empathy, and detail</span>. Think: what would make you smile if it were your pet?</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emotionalStorytellingHeart.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">Our service becomes unforgettable</span> when it tells a story — not just what we did, but how the pet felt and grew along the way.</p>
          </section>
        `
    },    
    homeAwayExperience: {
        title: "The “Home Away from Home” Experience",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/homeAwayExperienceTitle.png",
        body: `
          <section>
            <h3>🏡 What It Means</h3>
            <p>Pawsible isn’t just a care facility — <span class="highlight">it’s a second home</span>. From the moment pets enter, they should feel safe, soothed, and seen. And pet parents? They should feel at peace knowing their loved one is in nurturing hands.</p>
          </section>
      
          <section>
            <h4>🏠 Elements of “Home”</h4>
            <ul>
              <li>🛏️ <strong>Comfort</strong>: Soft beds, calm corners, clean crates — every space should invite rest</li>
              <li>🧠 <strong>Routine</strong>: Familiar faces, consistent schedules, personalized care</li>
              <li>🎵 <strong>Sensory Support</strong>: Gentle lighting, calming music, scent-free zones for sensitive pups</li>
              <li>👫 <strong>Staff Energy</strong>: <span class="highlight-blue">Calm voices, soft hands, and sincere presence</span> — that’s what creates trust</li>
            </ul>
            <p><span class="highlight-green">Consistency creates safety</span> — and our pets deserve both.</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/homeAwayExperiencePersonal.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🎨 Personal Touches</h4>
            <p>Details matter. A labeled cubby, a pup’s favorite blanket, a welcome note for new clients — these little moments are <span class="highlight-yellow">the big things</span>.</p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight">We are in the business of comfort and care</span>. Every pet should go home feeling even more relaxed than when they arrived — and every parent should feel like they’ve found a partner, not just a service.</p>
          </section>
        `
    },      
    orientationTour: {
        title: "Orientation & Tour SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/orientationTourTitle.png",
        body: `
          <section>
            <h3>🧭 Welcome Tour: Know the Space, Feel the Flow</h3>
            <p>
              Day 1 at Pawsible starts with more than a walk-through. 
              <span class="highlight">It’s your first step in understanding our rhythm, values, and safety-first mindset</span>. 
              Every zone exists for a reason — and by learning it, you’ll feel empowered to move with purpose.
            </p>
          </section>
      
          <section>
            <h4>📍 Key Areas You'll See</h4>
            <ul>
                <li>📠 <strong>Reception Area:</strong> Where first impressions happen — learn how we greet, communicate, and set the tone for every client interaction.</li>
                <li>🐾 <strong>Daycare & Play Yards:</strong> Supervised areas for enrichment, sorting zones by energy level, size, and play style.</li>
                <li>🛁 <strong>Grooming & Bathing Rooms:</strong> Calm, clean spaces with proper sanitization flows and safety handling protocols.</li>
                <li>🛏️ <strong>Boarding Suites & Quiet Rooms:</strong> Crate protocols, rest routines, and decompression setups for overnight guests.</li>
                <li>🎓 <strong>Training Zones:</strong> Structured areas for trainer-led sessions, shaping behaviors, and confidence building.</li>
                <li>🚐 <strong>Shuttle Loading Point:</strong> Where we greet and release pups for transport — safety and calmness are key.</li>
                <li>🧴 <strong>Sanitation Stations:</strong> Know where gloves, towels, wipes, and waste bins are — clean-as-you-go is the rule.</li>
                <li>🚻 <strong>Staff Bathrooms & Supply Closets:</strong> Stay organized, stocked, and respectful of shared spaces.</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/orientationTourWhy.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            <p><span class="highlight-blue">You’re not just learning where — you’re learning why.</span></p>
          </section>
      
          <section>
            <h4>📲 Your Daily Anchor: SOP & Guides</h4>
            <p>
              Inside the <strong>“SOP & Guides” tab of your staff dashboard</strong>, you’ll find everything from safety protocols to how we greet a nervous new guest.
              <span class="highlight-yellow">Bookmark it. Reference it. Rely on it.</span> 
              If something’s unclear — ask. If it’s written — read it fully.
            </p>
          </section>
      
          <section>
            <h4>🗺️ Final Thought</h4>
            <p>
              <span class="highlight-green">Walk the space. Feel the energy. Ask questions.</span> 
              Orientation is where your confidence begins. Know the layout, and you’ll unlock the flow. 
              Know the "why," and you’ll move like a pro.
            </p>
          </section>
        `
    },      
    policiesPayScheduling: {
        title: "Policies, Pay, & Scheduling",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/policiesAndSchedulesTitle.png",
        body: `
          <section>
            <h3>📋 What You Need to Know</h3>
            <ul>
              <li>⏱️ <strong>Clock In/Out</strong>: Use the staff dashboard daily. No verbal check-ins — it must be logged.</li>
              <li>🕒 <strong>Pay Schedule</strong>: Monthly via direct deposit. Check the dashboard for paystubs and status.</li>
              <li>📅 <strong>Shift Swaps & Time Off</strong>: Must be submitted through the dashboard and approved by a manager in advance.</li>
              <li>💬 <strong>Need Help?</strong> Ask HR via the dashboard support or during office hours.</li>
            </ul>
            <p><span class="highlight">Your time is valuable</span> — and we honor it with clarity, consistency, and respect.</p>
          </section>
      
          <section>
            <h4>📆 Schedules & Coverage</h4>
            <ul>
              <li>📌 Schedules are posted monthly on events in the dashboard.</li>
              <li>⚠️ Emergencies happen — but frequent no-shows or late arrivals without notice impact the whole team and may lead to reviews.</li>
              <li>🤝 Always confirm coverage if you're unable to attend a shift — <span class="highlight-yellow">it is your responsibility</span> until approved by management.</li>
            </ul>
          </section>
      
          <section>
            <h4>💸 Pay Details</h4>
            <ul>
              <li>💳 Direct deposit setup is required — update info anytime in your dashboard settings.</li>
              <li>🧾 Tip tracking (where applicable) is transparent and viewable by staff at any time.</li>
              <li>📈 Raises and bonuses are based on performance, attendance, etiquettes, and training milestones.</li>
            </ul>
            <p><span class="highlight-green">We grow together</span> — and that includes recognizing your time, effort, and impact.</p>
          </section>
      
          <section>
            <h4>🛟 Support Channels</h4>
            <ul>
              <li>🧠 Dashboard: First stop for forms, policy links, and schedule changes</li>
              <li>📨 HR: Contact through the “Help & Support” tab for questions, PTO, or conflicts</li>
              <li>🗣️ Anonymous feedback: Drop suggestions via the monthly feedback form (optional, but read by leadership!)</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-blue">Structure creates freedom</span>. When everyone shows up and communicates clearly, we make Pawsible the best workplace for both people and pets.</p>
          </section>
        `
    },      
    trainingPathway: {
        title: "Training Path & Certification Timeline",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingPathwayTitle.png",
        body: `
          <section>
            <h3>🎓 Grow With Purpose</h3>
            <p>At Pawsible, training is how we build safety, trust, and professionalism. It’s not just a checkbox — it’s how we care better, lead with confidence, and grow as a team.</p>
            <p>You'll use the <span class="highlight-blue">Training tab on your Staff Dashboard</span> to complete onboarding, track your progress, and unlock new roles as you get promoted.</p>
          </section>
      
          <section>
            <h4>📶 4 Stages of Training</h4>
            <ul>
              <li>👣 <strong>Stage 1: Shadow & Observe</strong> — Learn by watching experienced team members in action.</li>
              <li>🧠 <strong>Stage 2: Learn & Practice</strong> — Complete core concept modules and begin hands-on practice with support.</li>
              <li>📝 <strong>Stage 3: Review & Feedback</strong> — Get personalized feedback and coaching from team leads or trainers.</li>
              <li>🌟 <strong>Stage 4: Certification & Promotion Eligibility</strong> — Demonstrate your skills and unlock new responsibilities or tier raises.</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingPathwayFour.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            <p><span class="highlight-green">Every stage is designed to build confidence, consistency, and care.</span> Your growth is visible and supported.</p>
          </section>
      
          <section>
            <h4>🚀 Real Growth, Real Roles</h4>
            <p>Pawsible is expanding fast — which means more leadership and specialty roles opening up every month. We promote from within whenever possible.</p>
            <p><strong>Grooming, Training, and Managerial positions</strong> are available to team members who complete required training and show strong dedication.</p>
            <p>Consistency, professionalism, and a willingness to learn are what open doors here.</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingPathwayGrowth.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>💡 Why It Matters</h4>
            <p>Training isn’t busywork — it’s what sets Pawsible apart. It ensures safety, creates shared language, and builds a culture where everyone knows what “great care” looks like.</p>
            <p><span class="highlight">When you train well, pets thrive, clients trust you, and the team runs smoothly.</span></p>
          </section>
        `
    }      
  };
  